import React, { useContext, useMemo } from 'react';
import { Button, Dropdown, Nav, NavDropdown, Navbar } from 'react-bootstrap';
import { ArrowLeft, Building, Envelope, PersonCircle, QuestionCircle, Whatsapp } from 'react-bootstrap-icons';
import { LocalizeContext } from 'react-locale-language';
import { useHistory, useLocation } from 'react-router';
import { LANDING_URL } from '../../helpers/constants';
import { getUserFirstName, isCustomer, isSystemUser } from '../../helpers/global';
import { isRtl } from '../../helpers/localization';
import useAuth from '../../hooks/useAuth';
import LanguagePicker from './LanguagePicker';
import Logo from './Logo';
import { Link } from 'react-router-dom';

const adminRoutes = {
  initialPath: '/admin',
  paths: {
    customers: { route: '/manage-users/customer' },
    comp_table: { route: '/comp-table' }
  }
};
const otherUserRoutes = {
  initialPath: '/admin',
  paths: {
    customers: { route: '/manage-users/customer' },
    comp_table: { route: '/comp-table' }
  }
};

//ROUTES FOR OTHER ROLES COME HERE
const customerRoutes = {
  initialPath: '/customer',
  paths: {
    Dashboard: { route: '/' }
  }
};

const roleWiseRoutes = {
  Admin: adminRoutes,
  Manager: adminRoutes,
  Customer: customerRoutes,
  Salesperson: otherUserRoutes,
  'Financial Strategist': otherUserRoutes,
  'Real Estate Analyst': otherUserRoutes
};

const commonRoutes = user => [
  //routes common to all roles come here
];

const LinkItem = ({ dropdown = false, title, path, location, translate }) => {
  return dropdown ? (
    <NavDropdown.Item
      style={{ color: `${location.pathname}` === path ? 'white' : 'black' }}
      to={path}
      as={Link}
      active={`${location.pathname}` === path || `${location.pathname}/` === path}
    >
      {translate(title)}
    </NavDropdown.Item>
  ) : (
    <Nav.Link to={path} as={Link} active={`${location.pathname}` === path || `${location.pathname}/` === path}>
      {translate(title)}
    </Nav.Link>
  );
};

const NavRoutes = ({ user, role, location, translate }) => {
  const allRoutes = [roleWiseRoutes[role], ...commonRoutes(user)].filter(Boolean);

  return allRoutes.map(route => {
    const roleWisePaths = route.paths;
    const roleWiseInitialPath = route.initialPath;

    return Object.keys(roleWisePaths).map(headerName =>
      roleWisePaths[headerName].route ? (
        <LinkItem
          translate={translate}
          location={location}
          path={`${roleWiseInitialPath}${roleWisePaths[headerName].route}`}
          title={headerName}
        />
      ) : (
        <NavDropdown
          title={translate(headerName)}
          active={location.pathname.startsWith(`${roleWiseInitialPath}${roleWisePaths[headerName].initialPath}`)}
        >
          {roleWisePaths[headerName].routes.map((r, index) => (
            <LinkItem
              translate={translate}
              location={location}
              path={`${roleWiseInitialPath}${roleWisePaths[headerName].initialPath}${r.path}`}
              title={r.name}
              dropdown
            />
          ))}
        </NavDropdown>
      )
    );
  });
};

const Header = () => {
  const history = useHistory();
  const location = useLocation();
  const { translate, langCode } = useContext(LocalizeContext);

  const { user, viewAsUserMode, parentUser, logout } = useAuth();

  const role = useMemo(() => user?.role, [user]);

  const userName = useMemo(() => getUserFirstName(parentUser?.name), [parentUser]);

  const logoutLocal = history => {
    logout();
    history.push('/login');
  };

  return (
    <>
      <Navbar className="px-3" bg="white" expand="md">
        <Navbar.Brand href="/">
          <Logo noText small dark />
        </Navbar.Brand>
        <LanguagePicker />

        <>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            {role && (
              <Nav className={`ms-3 ${isCustomer(role) ? '' : 'me-md-auto'}`} style={{ fontSize: 14 }}>
                <NavRoutes user={user} role={role} location={location} translate={translate} />
              </Nav>
            )}
            {role && isCustomer(role) && (
              <Dropdown className={`d-inline-block me-md-auto ms-md-auto mb-1 mb-md-0`} style={{ zIndex: 8 }}>
                <Dropdown.Toggle
                  variant="outline-dark"
                  size="sm"
                  style={{ backgroundColor: 'transparent', color: 'black' }}
                  className="d-flex align-items-center"
                >
                  <QuestionCircle />
                  <span className="mb-0 smallFont px-2">{translate('need_help_talk_to_us')}</span>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    href={`mailto:${process.env.REACT_APP_EMAIL || 'office@ethica.finance'}`}
                    target="_blank"
                    className="smallFont d-flex align-items-center"
                  >
                    <Envelope className="mx-1" />
                    <span className="smallFont">{translate('email_us')}</span>
                  </Dropdown.Item>
                  <Dropdown.Item
                    href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '+9720533018468'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="smallFont text-success d-flex align-items-center"
                  >
                    <Whatsapp className="mx-1" />
                    <span className="smallFont">{translate('whatsapp_us')}</span>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
            <div className="d-flex align-items-center gap-1">
              {role && (
                <>
                  {isSystemUser(role) && (
                    <Nav.Link to={'/apartments'} as={Link}>
                      <Button size="sm" className="" variant="success text-white">
                        <Building className="mx-1" />
                        <span className="smallFont">{translate('apartments')}</span>
                      </Button>
                    </Nav.Link>
                  )}
                  <Button size="sm" className="" variant="primary-new" href={LANDING_URL}>
                    <ArrowLeft className="mx-1" />
                    <span className="smallFont">{translate('back_to_site')}</span>
                  </Button>
                  {viewAsUserMode && (
                    <Button className="" variant="outline-dark" size="sm">
                      <span className="smallFont mb-0">Viewing as {user?.name || 'N/A'}</span>
                    </Button>
                  )}{' '}
                  {!isCustomer(role) && (
                    <a className="" size="sm" href={'https://crm.ethica.finance/'} target="_blank">
                      <img
                        alt="App"
                        style={{ borderRadius: 5 }}
                        height={30}
                        width={30}
                        src={`${process.env.PUBLIC_URL}/CRMLogo.png`}
                      />
                    </a>
                  )}
                  <Dropdown
                    dir="rtl"
                    className={`d-none d-md-inline-block`}
                    style={{ zIndex: 8 }}
                    drop={isRtl(langCode) ? 'end' : 'start'}
                  >
                    <Dropdown.Toggle size="sm" variant="outline-dark">
                      <PersonCircle size={18} className="ms-2 align-text-top" />
                      <span className="mb-0 smallFont">{userName}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item className="smallFont" onClick={() => history.push('/profile')}>
                        {translate('view_profile')}
                      </Dropdown.Item>
                      <Dropdown.Item className="smallFont" onClick={() => logoutLocal(history)}>
                        {translate('logout')}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown className="d-inline-block d-md-none mt-1" style={{ zIndex: 8 }}>
                    <Dropdown.Toggle size="sm" variant="outline-dark">
                      <PersonCircle size={18} className="ms-2 align-text-top" />
                      <span className="mb-0 smallFont">{userName}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {!isCustomer(role) && (
                        <Dropdown.Item
                          className="border-bottom smallFont"
                          onClick={() => window.open('https://crm.ethica.finance/', '_target_blank')}
                        >
                          Ethica CRM
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item onClick={() => history.push('/profile')}>
                        {translate('view_profile')}
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => logoutLocal(history)}>{translate('logout')}</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </>
              )}
            </div>
          </Navbar.Collapse>
        </>
      </Navbar>
      <div
        style={{
          height: 0.5
        }}
        className="bg-dark"
      ></div>
    </>
  );
};

export default Header;
